import { ImmutableStore } from 'fluxible-immutable-utils';
import Actions from '../constants/Actions';
import Immutable from 'immutable';
import lunr from 'lunr';

const defaultState = {
  assignableUserRoles: [],
  managedUsers: [],
  id: '',
  emailAddress: '',
  firstName: '',
  lastName: '',
  active: true,
  title: '',
  roles: [],
  validationErrors: [],
  hasSentPasswordResetEmail: false,
  showNewUserCreatedReminder: false,
  showInactiveUsers: false,
  branchHierarchyTerms: [],
  branchHierarchy: [],
  searchTerm: ''
};

class UserManagementStore extends ImmutableStore {
  static storeName = 'UserManagementStore';

  static handlers = {

    [Actions.USER_MANAGEMENT__USER_HAD_ROLE_STATUS_TOGGLED]: 'userRoleToggled',
    [Actions.USER_MANAGEMENT__USER_HAD_ROLES_UPDATED]: 'saveUserRolesChange',

    [Actions.USER_MANAGEMENT__LIST_MANAGED_USERS]: 'listManagedUsers',
    [Actions.USER_MANAGEMENT__LIST_USER_ROLES]: 'listUserRoles',

    [Actions.USER_MANAGEMENT__USER_PII_UPDATED]: 'userPIIChanged',

    [Actions.USER_MANAGEMENT__USER_CREATED]: 'createNewUser',

    [Actions.USER_MANAGEMENT__SEARCH_TERM_CHANGED]: 'userSearchChanged',

    [Actions.USER_MANAGEMENT__VALIDATION_ERRORS_RECEIVED]: 'validationErrorUpdate',
    [Actions.USER_MANAGEMENT__VALIDATION_ERRORS_CLEARED] : 'resetErrors',
    [Actions.USER_MANAGEMENT__PASSWORD_RESET_EMAIL_SENT] : 'hasSentPasswordResetEmail',
    [Actions.USER_MANAGEMENT__PASSWORD_RESET_CONFIRMATION_DISPLAYED] : 'clearHasSentPasswordResetEmail',
    [Actions.USER_MANAGEMENT__USER_CREATED_REMINDER_DISPLAYED] : 'showNewUserCreatedReminder',
    [Actions.USER_MANAGEMENT__USER_CREATED_REMINDER_CLEARED] : 'clearNewUserCreatedReminder',
    [Actions.USER_MANAGEMENT__SHOW_INACTIVE_USERS_TOGGLED]: 'setShowInactiveUsers',

    [Actions.USER_MANAGEMENT__ORG_UNIT_HIERARCHY_AND_TERMS_LOADED]: 'setBranchHierarchyAndTerms',

  };

  constructor(dispatcher) {
    super(dispatcher);
    this.setState(defaultState);
    this.setupUserIndex();
  }


  getUserId() {
    return this.get('id');
  }

  getUserById(userId) {
    return this.getManagedUsers()
      .filter(u => u.get('id') === userId)
      .first();
  }

  getEmailAddress() {
    return this.get('emailAddress');
  }
  getFirstName() {
    return this.get('firstName');
  }
  getLastName() {
    return this.get('lastName');
  }
  getActive() {
    return this.get('active');
  }
  getTitle() {
    return this.get('title');
  }
  getHasSentPasswordResetEmail() {
    return this.get('hasSentPasswordResetEmail');
  }
  getShowNewUserCreatedReminder() {
    return this.get('showNewUserCreatedReminder');
  }
  getShowInactiveUsers() {
    return this.get('showInactiveUsers');
  }
  getRoles() {
    const roles = this.get('roles');
    if (roles) {
      return roles.toJS();
    } else {
      return [];
    }
  }
  getValidationErrors() {
    const errors = this.get('validationErrors');
    if (errors) {
      return errors.toJS();
    } else {
      return [];
    }
  }

  getManagedUsers() {
    const searchTerm = this.get('searchTerm');
    const managedUsers = this.get('managedUsers')
      .map(UserManagementStore._modeledUser);
    if (searchTerm) {
      const matched = this.userIndex.search(searchTerm);
      const ids = Immutable.Set(matched.map(m => m.ref));
      const foundUsers = managedUsers.filter(r => {
        return ids.contains(r.get('id'));
      });
      console.info('found user ', foundUsers.toJS());
      return foundUsers;
    } else {
      return managedUsers;
    }
  }

  getSearchTerm() {
    return this.get('searchTerm') || '';
  }

  getAssignableUserRoles() {
    const roles = this.get('assignableUserRoles');
    if (roles) {
      return roles.toJS();
    } else {
      return [];
    }
  }

  getBranchHierarchyTerms = () => {
    return this.get('branchHierarchyTerms');
  };

  getBranchHierarchy = () => {
    return this.get('branchHierarchy');
  };

  listManagedUsers(managedUsers) {
    // populate the store
    console.info('listing managed users in store ' + managedUsers.length);
    const users = Immutable.fromJS(managedUsers)
      .sort(UserManagementStore.comparator);
    this.mergeState({
      managedUsers: users
    });
    this.setupUserIndex(users.toJS());
  }

  listUserRoles(userRoles) {
    console.info('listing user roles', userRoles);
    const sorted = userRoles.sort((a, b) => ( String(a.name).localeCompare(String(b.name))));
    this.mergeState({
      assignableUserRoles: sorted.slice()
    });
  }

  setBranchHierarchyAndTerms = (data) => {
    console.info('UserManagementStore#setBranchHierarchyAndTerms', data);
    const { branchHierarchy, branchHierarchyTerms } = data;
    this.mergeState({ branchHierarchy, branchHierarchyTerms });
  };

  hasSentPasswordResetEmail() {
    console.info('hasSentPasswordResetEmail');
    this.mergeState({
      hasSentPasswordResetEmail: true
    });
  }
  clearHasSentPasswordResetEmail() {
    console.info('clearHasSentPasswordResetEmail');
    this.mergeState({
      hasSentPasswordResetEmail: false
    });
  }

  setShowInactiveUsers(shouldShow) {
    console.info('setShowInactiveUsers');
    this.mergeState({
      showInactiveUsers: shouldShow
    });
  }

  showNewUserCreatedReminder() {
    console.info('showNewUserCreatedReminder');
    this.mergeState({
      showNewUserCreatedReminder: true
    });
  }

  clearNewUserCreatedReminder() {
    console.info('clearNewUserCreatedReminder');
    this.mergeState({
      showNewUserCreatedReminder: false
    });
  }

  userRoleToggled(targetKey) {
    console.info('user role toggled ' + targetKey);
    const currentRoles = this.getRoles();
    const oldRole = currentRoles.find(r => r.key === targetKey);
    const newRole = {
      key: oldRole.key,
      value: oldRole.value,
      active: !oldRole.active,
      enabled: oldRole.enabled
    };
    const index = currentRoles.indexOf(oldRole);
    currentRoles.splice(index, 1, newRole);

    this.mergeState({
      roles: currentRoles
    });
  }

  saveUserRolesChange(newRoles) {
    console.info('save user roles change: ' + newRoles);
    this.mergeState({
      roles: newRoles
    });
  }

  userPIIChanged(rawUpdatedUser) {
    console.info('user pii changed with state: ', rawUpdatedUser);
    const updatedUser = UserManagementStore._immutableUser(rawUpdatedUser);
    const currentUsers = this.getManagedUsers();
    const index = currentUsers.findIndex(u => u.get('id') === updatedUser.get('id'));
    const updatedUsers = currentUsers.splice(index, 1, updatedUser).sort(UserManagementStore.comparator);
    this.mergeState({
      managedUsers: updatedUsers
    });
  }

  validationErrorUpdate(errors) {
    console.info('validationErrorUpdate', errors);
    this.mergeState({
      validationErrors: errors
    });
  }

  createNewUser(rawNewUser) {
    console.info('adding a new user', rawNewUser);
    const newUser = UserManagementStore._immutableUser(rawNewUser);
    const update = this.getManagedUsers()
      .push(newUser)
      .sort(UserManagementStore.comparator);
    this.mergeState({
      managedUsers: update
    });
  }

  static _immutableUser(user) {
    return UserManagementStore._modeledUser(
      Immutable.fromJS(user)
    );
  }

  static _modeledUser(immutableUser) {
    return immutableUser.updateIn(
      ['settings', 'selectedBranchHierarchy'],
      tier => tier ? tier.map(elem => elem.toSet()) : tier
    );
  }

  static comparator(a, b) {
    if (a.get('lastName') === b.get('lastName')) {
      return a.get('firstName') > b.get('firstName') ? 1 : -1;
    } else {
      return a.get('lastName') > b.get('lastName') ? 1 : -1;
    }
  }

  userSearchChanged(search) {
    this.mergeState({searchTerm: search});
  }

  setupUserIndex(users = []) {
    this.userIndex = lunr(function () {
      this.field('lastName', { boost: 5 });
      this.field('firstName', { boost: 4 });
      this.field('title', { boost: 3 });
      this.field('roles', { boost: 2 });
      this.field('emailAddress');
      users.forEach(user => {
        this.add(user);
      });
    });
  }

  resetErrors() {
    this.mergeState({validationErrors: []});
  }

  rehydrate(state) {
    const users = state.managedUsers || [];
    this.setupUserIndex(users);
    super.rehydrate(state);
  }

}
export default UserManagementStore;



// WEBPACK FOOTER //
// ./src/stores/UserManagementStore.js